import {
  Button,
  Modal,
  Progress,
  Space,
  Spin,
  Steps,
  Typography,
  Upload,
} from 'antd';
import {
  CloseCircleOutlined,
  InboxOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import keyBy from 'lodash/keyBy';
import Dragger from 'antd/es/upload/Dragger';
import { RcFile } from 'antd/es/upload';
import type { UploadProps } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { models } from '@ynomia/core';
import client from '../../../services/Client';
import { PRESET_TWIN_ID_NAME } from '../../../config/constants';
import ModalForm from '../../atoms/ModalForm';
import {
  getAssetTypes,
  getAssetTypesById,
  getScratchProjectCode,
  getTenant,
} from '../../../selectors';
import { ApsModel } from '../ModelEditor';
import {
  DEFAULT_MAP_LARGEST_ELEMENT_ONLY,
  defaultIsolationAssetType,
  defaultIsolationTwinIdField,
  mappingConfigFromForm,
  mappingFiltrexFunctions,
  mappingFormFields,
} from '../ModelEditor/mappingConfig';
import { canRemap, describeStatus } from '../ModelEditor/modelStatus';
import { getContextStores } from '../../../context';
import { notification } from '../../../antdProvider';
import styles from './styles.module.less';

export enum SetupStepsEnum {
  selectFile = 0,
  upload = 1,
  processing = 2,
  finish = 3,
}

export interface Props {
  apsModels?: Array<ApsModel>,
  modelsKeyedById: Record<string, models.ModelResponse>,
  savingInProgress: boolean,
  isSetupModalOpen: boolean,
  modelIdOverride?: string,
  stepOverride?: number,
  updateModel: (id: string, body: Partial<models.Model>) => Promise<void>,
  remapModel: (id: string) => Promise<void>,
  createModel: (body: Partial<models.Model>) => Promise<Partial<models.ModelResponse>>,
  setIsSetupModalOpen: (open: boolean) => void,
  /** Opens the new model in the edit screen, where it can be checked before enabling it. */
  onPreview: (modelId: string) => void,
}

const ModelSetupModal = ({
  apsModels,
  modelsKeyedById,
  savingInProgress,
  isSetupModalOpen,
  modelIdOverride,
  stepOverride,
  updateModel,
  remapModel,
  createModel,
  setIsSetupModalOpen,
  onPreview,
}: Props) => {
  const contextStores = getContextStores();
  const [uploadFile, setUploadFile] = useState<RcFile | undefined>();

  // Upload Modal States
  const [uploadModelId, setUploadModelId] = useState<string | undefined>(modelIdOverride);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const [uploadStep, setUploadStep] = useState<number>(stepOverride || SetupStepsEnum.selectFile);
  const uploadStarted = useRef(false);

  /* Selectors */
  const projectCode = getScratchProjectCode(contextStores);
  const tenant = getTenant(contextStores);
  const assetTypes = getAssetTypes(contextStores);
  const assetTypesKeyedById = getAssetTypesById(contextStores);

  const apsModelsKeyedByObjectKey = keyBy(apsModels, 'objectKey');
  const model = modelsKeyedById[uploadModelId!];
  const apsModelManifest = apsModelsKeyedByObjectKey[model?.fileName]?.manifest;
  const translationProgress = apsModelManifest?.progress;
  const status = describeStatus(model?.status);
  const messages = apsModelManifest
    ?.derivatives
    ?.map(d => d.messages)
    ?.flat();

  useEffect(() => {
    if (!isSetupModalOpen) return;
    setUploadStep(stepOverride || SetupStepsEnum.selectFile);
    setUploadPercent(0);
    setUploadFile(undefined);
    setUploadModelId(modelIdOverride);
    uploadStarted.current = false;
  }, [isSetupModalOpen]);

  useEffect(() => {
    if (uploadStep !== SetupStepsEnum.processing) return;
    if (model?.status === 'mapped') setUploadStep(SetupStepsEnum.finish);
  }, [model?.status, uploadStep]);

  const uploadFailed = (e?: any) => {
    notification.error({
      message: 'upload failed, please try again',
      description: e?.response?.data?.err?.msg || e?.message,
    });
    setIsSetupModalOpen(false);
  };

  const startUploadFile = async () => {
    if (!model?.fileName || !uploadModelId) return;
    try {
      const signedUrlResp = await client.server.get(
        `/scratch/models/${tenant}/${projectCode}/signedUrl/${model.fileName}`,
      );
      const { uploadKey, urls } = signedUrlResp.data.value;
      // Throws on a failed upload, as do the requests either side of it.
      await axios.put(
        urls[0],
        uploadFile,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: e => setUploadPercent(Math.round((e.loaded / e.total) * 100)),
        },
      );
      const completeSignedUploadResp = await client.server.post(
        `/scratch/models/${tenant}/${projectCode}/completeSignedUpload/${model.fileName}`,
        { uploadKey },
      );
      const { translation } = completeSignedUploadResp.data.value;
      if (translation?.result !== 'success') {
        uploadFailed();
        return;
      }
      // Status moves server-side from here: translating, then mapping, then mapped.
      await updateModel(uploadModelId, { urn: translation.urn });
      setUploadStep(SetupStepsEnum.processing);
    } catch (e: any) {
      uploadFailed(e);
    }
  };

  // Also waits for the new model to reach the list: a poll that overtakes the create's own
  // refresh can leave it out until the next one.
  useEffect(() => {
    if (uploadStep !== SetupStepsEnum.upload || !model?.fileName || uploadStarted.current) return;
    uploadStarted.current = true;
    startUploadFile();
  }, [uploadStep, model?.fileName]);

  const getExtensionFromFileName = (name: string) => name.split('.').pop() as string;

  const acceptedFiles = ['rvt', 'RVT', 'ifc', 'IFC'];
  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: acceptedFiles.map(e => `.${e}`).join(','),
    beforeUpload: (file) => {
      const extension = getExtensionFromFileName(file.name);
      if (!acceptedFiles.includes(extension)) {
        notification.error({
          message: 'Error',
          description: 'File must be of .rvt or .ifc format',
        });
        return Upload.LIST_IGNORE;
      }
      setUploadFile(file);
      // Prevent upload
      return false;
    },
    fileList: uploadFile
      ? [{
        uid: '1',
        name: uploadFile.name,
        status: 'done',
        url: '',
      }]
      : [],
    style: {
      marginTop: 30,
    },
    disabled: savingInProgress,
  };

  // The mapping settings start at the defaults. Without precast, the location twin ID key
  // depends on the Twin ID key chosen here, so it's left blank to follow it.
  const formDefaults = {
    mappingFilters: '',
    mapLargestElementOnly: DEFAULT_MAP_LARGEST_ELEMENT_ONLY,
    isolationAssetType: defaultIsolationAssetType(assetTypes),
    isolationTwinIdField: defaultIsolationTwinIdField(assetTypes),
  };

  const onDetailsSubmit = async (e: { [id: string]: any }) => {
    const extension = getExtensionFromFileName(uploadFile!.name);
    const nameAssetTypes = e.assetTypes.map(t => assetTypesKeyedById.get(t)?.name?.toLowerCase());
    const nameContent = [
      ...nameAssetTypes,
      e.building,
      e.softwareVersion,
      e.version,
      dayjs().format('YYYY-MM-DD'),
    ].join('_');
    const name = `${nameContent}.${extension}`;

    if (apsModelsKeyedByObjectKey[name] || modelsKeyedById[name]) {
      notification.error({
        message: 'Error',
        description: `A model with the same version already exists.
        Please provide a unique version number.`,
      });
      return;
    }

    const newModel: Partial<models.Model> = {
      fileName: name,
      urn: '',
      mapping: {},
      assetTypes: e.assetTypes,
      twinIdField: e.twinIdField,
      originalFileName: uploadFile?.name || '',
      date: new Date(),
      // Saved before upload: mapping runs on the server as soon as translation finishes.
      ...mappingConfigFromForm(e, assetTypes, e.assetTypes),
    };
    let newDocument: Partial<models.ModelResponse>;
    try {
      newDocument = await createModel(newModel);
    } catch (err: any) {
      notification.error({
        message: 'Could not create the model',
        description: err?.response?.data?.err?.msg || err?.message,
      });
      return;
    }
    setUploadModelId(newDocument.id);
    setUploadStep(SetupStepsEnum.upload);
  };

  const urlSafe = (id: string) => ({
    condition: `not isSafeUrlParameter(${id} of values)`,
    message: 'This must not contain spaces or special characters',
  });

  const selectFileStepContent = (
    <>
      <Dragger {...draggerProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag a file to this area to upload
        </p>
        <p className="ant-upload-hint">
          &#34;.rvt&#34; and &#34;.ifc&#34; files supported
        </p>
      </Dragger>
      <ModalForm
        fields={[
          {
            disabled: false,
            entryComponent: 'text',
            id: 'building',
            isRequired: true,
            properties: {
              placeholder: `ie. ${projectCode}, building a, podium`,
            },
            validationConditions: [urlSafe('building')],
            label: 'Model Name / Building(s)',
          },
          {
            entryComponent: 'picklist',
            id: 'assetTypes',
            label: 'Asset Type(s)',
            properties: {
              options: assetTypes.map(assetType => ({
                label: assetType.label,
                value: assetType.id,
              })),
              multi: true,
            },
            isRequired: true,
          },
          {
            entryComponent: 'picklist',
            id: 'twinIdField',
            label: 'Twin ID Property Key',
            properties: {
              options: PRESET_TWIN_ID_NAME.map(label => ({
                label,
                value: label,
              })),
              tags: true,
              maxCount: 1,
            },
            isRequired: true,
          },
          {
            label: 'divider',
            id: 'divider',
            entryComponent: 'divider',
          },
          {
            disabled: false,
            entryComponent: 'text',
            id: 'version',
            isRequired: true,
            properties: {
              placeholder: 'ie. V1',
            },
            validationConditions: [urlSafe('version')],
            label: 'Model Version',
          },
          {
            disabled: false,
            entryComponent: 'text',
            id: 'softwareVersion',
            isRequired: true,
            properties: {
              placeholder: 'ie. 2020',
            },
            validationConditions: [urlSafe('softwareVersion')],
            label: 'Revit/Software Version',
          },
          {
            label: 'divider',
            id: 'mappingDivider',
            entryComponent: 'divider',
          },
          ...mappingFormFields(assetTypes),
        ]}
        submitButtonText="Proceed to upload"
        onSubmit={onDetailsSubmit}
        isDisabled={savingInProgress}
        disableSubmitButton={!uploadFile}
        hideCancelButton
        submitButtonLoading={savingInProgress}
        defaultValues={formDefaults}
        // Takes what the drop area and steps leave, so the fields scroll, not the page.
        fill
        extraFiltrexFunctions={{
          isSafeUrlParameter: str => str && encodeURIComponent(str) === str,
          ...mappingFiltrexFunctions,
        }}
      />
    </>
  );

  // One block, so the step's flex column doesn't put the spinner on its own line.
  const uploadStepContent = (
    <div>
      Upload in progress...
      {(uploadPercent === 0 || uploadPercent === 100) && (
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />}
        style={{ marginLeft: 10 }}
      />
      )}
      <Progress percent={uploadPercent} type="line" />
    </div>
  );

  const [retrying, setRetrying] = useState<boolean>(false);
  const onRetryMapping = async () => {
    setRetrying(true);
    try {
      await remapModel(uploadModelId!);
    } finally {
      setRetrying(false);
    }
  };

  const processingInProgressContent = (
    <div className={styles.stepContainer}>
      <div className={styles.translationContainer}>
        The model is being translated and mapped in the cloud.
        <br />
        <br />
        You may wait until it&#39;s finished or close
        the page and come back later to complete setup.
        <br />
      </div>
      <LoadingOutlined spin />
      <br />
      {model?.status === 'translating' || !model?.status
        ? `Translating${translationProgress ? ` - ${translationProgress}` : ''}`
        : status?.label}
      <br />
      <br />
    </div>
  );

  const processingFailedContent = (
    <div className={styles.stepContainer}>
      <div className={styles.translationContainer}>
        {model?.status === 'mapping_failed'
          ? 'The model failed to map.'
          : 'The model failed to translate.'}
        <br />
        <br />
        {model?.statusError}
        <br />
      </div>
      <CloseCircleOutlined style={{ fontSize: 24, color: 'red' }} />
      <br />
      {model?.status === 'translation_failed' && messages?.map(m => (
        <div key={m?.message}>
          <b>{m?.code || ''}</b>
          <br />
          {m?.message || ''}
        </div>
      ))}
      {canRemap(model?.status) && (
        <Button type="primary" onClick={onRetryMapping} loading={retrying}>
          Retry mapping
        </Button>
      )}
    </div>
  );

  // Enabling is left to the table's Enabled column, the one place models are published.
  const finishStepContent = (
    <div className={styles.finishContentContainer}>
      <Typography.Text strong>Setup complete.</Typography.Text>
      <Typography.Paragraph style={{ marginTop: 8 }}>
        Use the
        {' '}
        <b>Enabled</b>
        {' '}
        column to publish or hide this model.
      </Typography.Paragraph>
      <Space>
        <Button onClick={() => onPreview(uploadModelId!)}>Preview</Button>
        <Button type="primary" onClick={() => setIsSetupModalOpen(false)}>Close</Button>
      </Space>
    </div>
  );

  const items: Array<{
    key: number,
    title: string,
    content: React.ReactNode
  }> = [
    {
      key: SetupStepsEnum.selectFile,
      title: 'Select File',
      content: selectFileStepContent,
    },
    {
      key: SetupStepsEnum.upload,
      title: 'Upload',
      content: uploadStepContent,
    },
    {
      key: SetupStepsEnum.processing,
      title: 'Processing',
      content: status?.failed ? processingFailedContent : processingInProgressContent,
    },
    {
      key: SetupStepsEnum.finish,
      title: 'Finish',
      content: finishStepContent,
    },
  ];

  return (
    <Modal
      title="Add Model"
      open={isSetupModalOpen}
      width={1000}
      footer={null}
      onCancel={() => setIsSetupModalOpen(false)}
      closable={uploadStep !== SetupStepsEnum.upload}
      maskClosable={false}
      // Each open starts with a fresh form.
      destroyOnClose
      // Capped to the screen (less the modal's offset, header and padding), with each level a
      // flex column, so the select-file form can take the height that's left.
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div className={styles.setupContainer}>
        <Steps current={uploadStep} items={items} style={{ width: 800 }} />
        <div className={styles.stepContent}>{items[uploadStep].content}</div>
      </div>
    </Modal>
  );
};

export default ModelSetupModal;
